import {ApolloClient, ApolloLink} from '@apollo/client';
import {finalize} from 'rxjs';

/**
 * A `fetch`-compatible function. Defined locally so this always-on instrument
 * carries no dependency on the (flag-gated, local-only) render-profiler tooling.
 */
export type ProfilableFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

/**
 * CONT-5981 Phase P — always-on dynamo load counters.
 *
 * A tiny, always-on instrument that counts the client GraphQL round trips a CMS
 * Collection Template page pays for, so we can baseline the pathology in prod
 * RUM *before* the CONT-5981 batching/coalescing fixes ship. It is deliberately
 * NOT the `render-profiler`: that instrument is flag-gated, allocation-heavy
 * (JSON.stringify, body parsing, heap reads) and dormant in prod. These counters
 * are cheap enough to run on every session — integer increments plus a few
 * `performance.now()` reads — and only ever accumulate while a "load window" is
 * open (opened by the Designer shell bridge). When no window is open every probe
 * is a single null check.
 *
 * Two windows, mutually exclusive in time:
 * - cold load  — the initial dynamo burst when a CMS template page opens.
 * - save window — the `resetStore()` refetch burst re-paid on an in-session save.
 *
 * The operation counter (`countingLink`) sits ABOVE Apollo's `BatchHttpLink`,
 * so it counts GraphQL *operations*. The HTTP-request counter (`wrapFetch`) sits
 * BELOW the batch link, so it counts the *fetches* those operations coalesced
 * into — the realized batch effectiveness.
 *
 * All counting state is encapsulated per Apollo client by
 * `createDynamoLoadMetrics()`: each client gets its own closure, so there is no
 * shared module-level singleton and no hidden "one client per realm" assumption.
 * `createApolloClient` wires an instance and registers it in a client-keyed
 * WeakMap; consumers retrieve it with `getDynamoLoadMetrics(client)`.
 */

function nowMs(): number {
  return typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

type LoadWindow = {
  operations: number;
  httpRequests: number;
  /** `performance.now()` start of the first-started operation in this window. */
  firstOpStartMs: number | null;
  /** `performance.now()` end of the last-completed operation in this window. */
  lastOpEndMs: number | null;
  /** duration of the first-started (primary-item) operation. */
  primaryItemMs: number | null;
};

type SaveWindow = LoadWindow & {
  /** wall-clock start of the reset that opened this window. */
  startedAtMs: number;
  /** number of resets coalesced into this window (starts at 1). */
  resetCount: number;
};

export type CmsTemplateDynamoLoadMetrics = {
  graphqlOperations: number;
  httpRequests: number;
  batchSize: number;
  primaryItemMs: number;
  refSpanMs: number;
};

export type CmsTemplateSaveRefetchMetrics = {
  operationsRefetched: number;
  httpRequests: number;
  refetchDurationMs: number;
  resetCount: number;
};

type ActiveTarget = 'cold' | 'save' | null;

/**
 * Binds an in-flight operation to the window that was open when it *started*, so
 * its completion is attributed to that window even if the active target has since
 * switched (e.g. a save window opened while a cold-load op was still resolving).
 * `window` is null when no window was open at start, in which case the operation
 * is not counted at all.
 */
export type DynamoOperationHandle = {
  startMs: number;
  window: LoadWindow | null;
};

function emptyWindow(): LoadWindow {
  return {
    operations: 0,
    httpRequests: 0,
    firstOpStartMs: null,
    lastOpEndMs: null,
    primaryItemMs: null,
  };
}

/**
 * The counting surface for a single Apollo client. `createApolloClient` attaches
 * `countingLink` above the batch link and `wrapFetch` below it; the Designer
 * shell bridge drives the load windows via `getDynamoLoadMetrics(client)`. The
 * recording methods are the primitives the link and fetch wrapper call — exposed
 * so tests can drive counting without a live Apollo pipeline.
 */
export type DynamoLoadMetrics = {
  getActiveLoadTarget(): 'cold' | 'save' | null;
  getLastDynamoOperationStartMs(): number;
  setOnDynamoOperationStart(cb: (() => void) | null): void;
  startColdLoad(): void;
  endColdLoad(): void;
  startSaveWindow(): void;
  endSaveWindow(): void;
  recordDynamoOperationStart(): DynamoOperationHandle;
  recordDynamoOperationEnd(handle: DynamoOperationHandle): void;
  recordDynamoHttpRequest(): void;
  readColdLoad(): CmsTemplateDynamoLoadMetrics | null;
  readSaveRefetch(): CmsTemplateSaveRefetchMetrics | null;
  /**
   * Always-on ApolloLink that counts dynamo GraphQL operations. Attach it ABOVE
   * the batch link (via `customLinks`) so it sees operations, not batched HTTP
   * requests.
   */
  countingLink: ApolloLink;
  /**
   * Wrap a `fetch` so every actual HTTP request Apollo's `BatchHttpLink` makes is
   * counted below the batch link. Increment-only: it never reads or parses the
   * body. Zero-effect when no load window is open.
   */
  wrapFetch(baseFetch?: ProfilableFetch): ProfilableFetch;
};

/**
 * Create a fresh, self-contained set of dynamo load counters. Each JS realm (the
 * Designer shell frame and the separate-origin canvas frame) creates its own via
 * its own Apollo client, which is what we want: a frame counts only its own round
 * trips. All state below lives in this closure — no module-level singleton.
 */
export function createDynamoLoadMetrics(): DynamoLoadMetrics {
  let coldWindow: LoadWindow | null = null;
  let saveWindow: SaveWindow | null = null;
  let activeTarget: ActiveTarget = null;
  let lastOperationStartMs = 0;
  let onOperationStart: (() => void) | null = null;

  function currentWindow(): LoadWindow | null {
    if (activeTarget === 'save') {
      return saveWindow;
    }
    if (activeTarget === 'cold') {
      return coldWindow;
    }
    return null;
  }

  /**
   * Which load window, if any, is currently accumulating. The Designer shell
   * bridge uses it to decide whether to arm a quiescence flush on network idle.
   */
  function getActiveLoadTarget(): 'cold' | 'save' | null {
    return activeTarget;
  }

  /**
   * `performance.now()` timestamp of the most recent operation start (across all
   * windows). The quiescence detector uses it to tell a settled burst apart from
   * a mid-waterfall lull.
   */
  function getLastDynamoOperationStartMs(): number {
    return lastOperationStartMs;
  }

  /**
   * Register a callback invoked synchronously whenever a dynamo operation starts.
   * The Designer shell bridge uses it to cancel a pending quiescence flush when a
   * new wave of the dependent round-trip waterfall begins. A single slot is
   * enough — only the shell frame arms a flush.
   */
  function setOnDynamoOperationStart(cb: (() => void) | null): void {
    onOperationStart = cb;
  }

  /** Open a fresh cold-load window and make it the active counting target. */
  function startColdLoad(): void {
    coldWindow = emptyWindow();
    activeTarget = 'cold';
  }

  /** Close the cold-load window, discarding its counters. */
  function endColdLoad(): void {
    coldWindow = null;
    if (activeTarget === 'cold') {
      activeTarget = saveWindow ? 'save' : null;
    }
  }

  /**
   * Open a save-refetch window, or coalesce into the one already open. Rapid
   * successive resets (a save fans out to ~17 reset-triggering actions) collapse
   * into a single window whose `resetCount` records how many were coalesced.
   */
  function startSaveWindow(): void {
    if (saveWindow) {
      saveWindow.resetCount += 1;
    } else {
      saveWindow = {...emptyWindow(), startedAtMs: nowMs(), resetCount: 1};
    }
    activeTarget = 'save';
  }

  /** Close the save-refetch window, discarding its counters. */
  function endSaveWindow(): void {
    saveWindow = null;
    if (activeTarget === 'save') {
      activeTarget = coldWindow ? 'cold' : null;
    }
  }

  /**
   * Record the start of a dynamo operation. Returns a handle to hand back to
   * `recordDynamoOperationEnd`; it captures the window open *right now* so the
   * completion is attributed to that window rather than whichever is active when
   * the operation finalizes. Always cheap: it bumps the last-activity timestamp
   * and notifies the flush canceller even when no window is open.
   */
  function recordDynamoOperationStart(): DynamoOperationHandle {
    const start = nowMs();
    lastOperationStartMs = start;
    onOperationStart?.();
    const window = currentWindow();
    if (window && window.firstOpStartMs === null) {
      window.firstOpStartMs = start;
    }
    return {startMs: start, window};
  }

  /** Record the completion of a dynamo operation started via its handle. */
  function recordDynamoOperationEnd({
    startMs,
    window,
  }: DynamoOperationHandle): void {
    if (!window) {
      return;
    }
    const end = nowMs();
    window.operations += 1;
    if (window.lastOpEndMs === null || end > window.lastOpEndMs) {
      window.lastOpEndMs = end;
    }
    if (window.firstOpStartMs === null) {
      window.firstOpStartMs = startMs;
    }
    // The primary item is the first-started operation of the window; the
    // dependent referenced-item waterfall only fires after it resolves. Keying
    // off the earliest start (not the first completion) stays correct even if a
    // referenced op happens to complete first.
    if (startMs <= window.firstOpStartMs) {
      window.primaryItemMs = end - startMs;
    }
  }

  /** Record one HTTP request (fetch) below the batch link. */
  function recordDynamoHttpRequest(): void {
    const window = currentWindow();
    if (!window) {
      return;
    }
    window.httpRequests += 1;
  }

  /**
   * Read the accumulated cold-load metrics, or `null` if no operations landed in
   * the window (so the caller can wait for the burst instead of emitting an empty
   * event). `refSpanMs` is the wall-clock span of the whole burst minus the
   * primary item — the referenced-item "component amplifier" cost.
   */
  function readColdLoad(): CmsTemplateDynamoLoadMetrics | null {
    const window = coldWindow;
    if (!window || window.operations === 0) {
      return null;
    }
    const primaryItemMs = window.primaryItemMs ?? 0;
    const span =
      window.firstOpStartMs !== null && window.lastOpEndMs !== null
        ? window.lastOpEndMs - window.firstOpStartMs
        : 0;
    const refSpanMs = Math.max(0, span - primaryItemMs);
    const httpRequests = window.httpRequests;
    const batchSize =
      httpRequests > 0 ? window.operations / httpRequests : window.operations;
    return {
      graphqlOperations: window.operations,
      httpRequests,
      batchSize: round(batchSize),
      primaryItemMs: round(primaryItemMs),
      refSpanMs: round(refSpanMs),
    };
  }

  /**
   * Read the accumulated save-refetch metrics, or `null` if no operations landed
   * in the window. `refetchDurationMs` is the wall-clock span from the reset that
   * opened the window to the end of the last refetch operation — the re-paid
   * network cost of the save. It deliberately measures to the last operation's
   * completion rather than to read/flush time, so the flush's quiescence debounce
   * (and any idle gap after the burst settles) is excluded and the value is a
   * true refetch duration, consistent with the cold-load `refSpanMs` derivation.
   */
  function readSaveRefetch(): CmsTemplateSaveRefetchMetrics | null {
    const window = saveWindow;
    if (!window || window.operations === 0) {
      return null;
    }
    // operations > 0 guarantees at least one recorded end, so lastOpEndMs is set;
    // fall back to startedAtMs (duration 0) only defensively.
    const refetchEndMs = window.lastOpEndMs ?? window.startedAtMs;
    return {
      operationsRefetched: window.operations,
      httpRequests: window.httpRequests,
      refetchDurationMs: round(refetchEndMs - window.startedAtMs),
      resetCount: window.resetCount,
    };
  }

  const countingLink = new ApolloLink((operation, forward) => {
    const handle = recordDynamoOperationStart();
    return forward(operation).pipe(
      finalize(() => recordDynamoOperationEnd(handle))
    );
  });

  function wrapFetch(baseFetch?: ProfilableFetch): ProfilableFetch {
    return (input, init) => {
      recordDynamoHttpRequest();
      const doFetch: ProfilableFetch =
        baseFetch ??
        ((requestInput, requestInit) => fetch(requestInput, requestInit));
      return doFetch(input, init);
    };
  }

  return {
    getActiveLoadTarget,
    getLastDynamoOperationStartMs,
    setOnDynamoOperationStart,
    startColdLoad,
    endColdLoad,
    startSaveWindow,
    endSaveWindow,
    recordDynamoOperationStart,
    recordDynamoOperationEnd,
    recordDynamoHttpRequest,
    readColdLoad,
    readSaveRefetch,
    countingLink,
    wrapFetch,
  };
}

// Associates each Apollo client with its own counters. Weak keys let a client
// (and its metrics) be garbage-collected together once nothing else holds it.
const metricsByClient = new WeakMap<ApolloClient, DynamoLoadMetrics>();

/** Register a client's counters so consumers can look them up by client. */
export function associateDynamoLoadMetrics(
  client: ApolloClient,
  metrics: DynamoLoadMetrics
): void {
  metricsByClient.set(client, metrics);
}

/**
 * The dynamo load counters for a given Apollo client, or `undefined` if the
 * client was created without `countDynamoLoadMetrics`.
 */
export function getDynamoLoadMetrics(
  client: ApolloClient
): DynamoLoadMetrics | undefined {
  return metricsByClient.get(client);
}
